import { GoogleGenerativeAI } from '@google/generative-ai';
import type { FormData } from '../types/form';

function initializeGemini() {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
  if (!apiKey) {
    throw new Error('Missing Gemini API key');
  }

  return new GoogleGenerativeAI(apiKey);
}

export async function generateEnhancedStory(formData: FormData): Promise<string> {
  try {
    const genAI = initializeGemini();
    const model = genAI.getGenerativeModel({ model: 'gemini-2.5-flash' });

    // Validate required fields
    const requiredFields = ['title', 'date', 'location', 'department', 'description'];
    for (const field of requiredFields) {
      if (!formData[field]?.trim()) {
        throw new Error(`Missing required field: ${field}`);
      }
    }

    const prompt = `
      You are a professional journalist writing for a publication with the tone and style of Channel NewsAsia. Your task is to transform user-provided details into a well-structured news article using the inverted pyramid format—starting with the most critical information and gradually adding details.
      
      EVENT DETAILS:
      - Title: ${formData.title?.trim()}
      - Date: ${formData.date?.trim()}
      - Location: ${formData.location?.trim()}
      - Department/Team: ${formData.department?.trim()}
      ${formData.objective ? `- Objective: ${formData.objective.trim()}` : ''}
      ${formData.participants ? `- Key Participants: ${formData.participants.trim()}` : ''}
      
      STORY CONTENT:
      ${formData.description?.trim()}
      
      ${formData.challenges ? `CHALLENGES:\n${formData.challenges.trim()}` : ''}
      ${formData.outcomes ? `OUTCOMES:\n${formData.outcomes.trim()}` : ''}
      ${formData.quotes ? `QUOTES:\n${formData.quotes.trim()}` : ''}
      ${formData.takeaways ? `KEY TAKEAWAYS:\n${formData.takeaways.trim()}` : ''}
      
      WRITING GUIDELINES:
      1. Use British grammar and spelling.
2. Write in active voice for clarity and engagement.
3. Maintain a factual and authoritative tone, ensuring objectivity while keeping the content engaging.
4. Structure the article with appropriate subheadings when necessary, and use bullet points for clarity when presenting lists or summarised information.
5. If a user has not provided a quote, generate a realistic and relevant one based on the context.
6. Ensure coherence and logical flow, filling in gaps where needed while staying faithful to the given details.
    `;

    let generatedText = '';
    try {
      const result = await model.generateContent(prompt.trim());
      const response = await result.response;
      generatedText = response.text().trim();
      
      if (!generatedText) {
        throw new Error('Generated story is empty');
      }
    } catch (apiError) {
      console.error('Gemini API error:', apiError);
      throw new Error('Failed to generate story using Gemini API');
    }
    
    return generatedText;
  } catch (error) {
    console.error('Error generating story with Gemini:', error);
    
    // Fallback to basic story generation if Gemini fails
    return `# ${formData.title?.trim() || 'Story'}

On ${formData.date} at ${formData.location}, ${formData.department} ${formData.description}

${formData.challenges ? `## Challenges\n${formData.challenges}\n` : ''}
${formData.outcomes ? `## Outcomes\n${formData.outcomes}\n` : ''}
${formData.quotes ? `## Quotes\n${formData.quotes}\n` : ''}
${formData.takeaways ? `## Key Takeaways\n${formData.takeaways}` : ''}`;
  }
}